import { debounce, isEqual } from "lodash-es";
import type { FetchOptions } from "ofetch";
import type { PaginatedResponse } from "@/utils/types";
import { encodeFilterHash, filterQueryFromHash } from "@/utils/filter";

export async function useAsyncDataE<T>(handler: () => Promise<T>, options?: { deep?: boolean }): Promise<Ref<T>> {
  const nuxtApp = useNuxtApp();
  const createRef: (d: T) => Ref<T> = options?.deep ? ref : shallowRef;
  const dataRef = createRef(null as any);

  async function runHandler() {
    try {
      dataRef.value = await handler();
    } catch (error: any) {
      error.fatal = true;
      throw createError(error);
    }
  }

  const unsubscribeDataRefreshHook = nuxtApp.hook('app:data:refresh', runHandler);
  onScopeDispose(() => {
    unsubscribeDataRefreshHook();
  });

  await runHandler();
  return dataRef;
}

export async function useFetchE<T>(url: string, options: Parameters<typeof $fetch<T>>[1] & { deep?: boolean }): Promise<Ref<T>> {
  return useAsyncDataE(() => $fetch(url, options), options);
}

export function useCursorPaginationFetcher<T>({ baseURL, query }: { baseURL: string|null, query?: object }) {
  const searchParams = new URLSearchParams((baseURL || '').split('?')[1]);
  for (const [k, v] of Object.entries(query || {})) {
    if (v) {
      if (Array.isArray(v)) {
        searchParams.delete(k);
        for (const e of v) {
          searchParams.append(k, e);
        }
      } else {
        searchParams.set(k, v);
      }
    } else {
      searchParams.delete(k);
    }
  }
  if (baseURL) {
    baseURL = baseURL.split('?')[0] + '?' + searchParams.toString();
  }

  const hasBaseURL = computed(() => !!baseURL);
  const nextPageURL = ref<string|null>(baseURL);
  const hasNextPage = computed(() => !!nextPageURL.value);
  const pending = ref(false);
  const data = ref<T[]>([]);
  const error = ref<any|null>(null);
  const hasError = computed(() => error.value !== null);

  async function fetchNextPage() {
    if (pending.value || !hasNextPage.value) {
      return Promise.resolve();
    }

    try {
      pending.value = true;

      const res = await $fetch<PaginatedResponse<T>>(nextPageURL.value!, { method: 'GET' });
      nextPageURL.value = res.next;
      data.value.push(...toRef(res.results).value);
      error.value = null;
    } catch (err: any) {
      error.value = err?.data || 'Could not load data';
      requestErrorToast({ error: err });
    } finally {
      pending.value = false;
    }
  }

  return {
    baseURL,
    data,
    error,
    pending,
    hasError,
    hasNextPage,
    hasBaseURL,
    fetchNextPage,
  };
}

export function useSearchableCursorPaginationFetcher<T>(options: { baseURL: string|null, query?: object }) {
  const initializingFetcher = ref(false);
  const fetcher = ref<ReturnType<typeof useCursorPaginationFetcher<T>>>(null as any);
  const fetchNextPageDebounced = debounce(async () => {
    await fetcher.value.fetchNextPage();
    initializingFetcher.value = true;
  }, 750);
  function createFetcher(options: { baseURL: string|null, query?: object, fetchInitialPage?: boolean, debounce?: boolean }) {
    const newFetcher = useCursorPaginationFetcher<T>(options) as any;
    if (options.fetchInitialPage) {
      initializingFetcher.value = true;
      if (options.debounce) {
        fetchNextPageDebounced();
      } else {
        fetchNextPage();
      }
    }
    fetcher.value = newFetcher
  }
  createFetcher(options);

  async function fetchNextPage() {
    fetchNextPageDebounced!();
    await fetchNextPageDebounced!.flush();
    initializingFetcher.value = false;
  }

  const currentQuery = computed(() => {
    return Object.fromEntries(new URLSearchParams((fetcher.value.baseURL || '').split('?')[1] || ''));
  });

  function applyFilters(query: object, { fetchInitialPage = true, debounce = false } = {}) {
    const newQuery = { ...currentQuery.value, ...query };
    if (isEqual(currentQuery.value, newQuery)) {
      return;
    }
    createFetcher({ baseURL: fetcher.value.baseURL, query: newQuery, fetchInitialPage, debounce });
  }

  const search = computed({
    get: () => currentQuery.value.search || '',
    set: (val: string) => applyFilters({ search: val }, { debounce: true }),
  });

  function reset(options: { baseURL: string|null, query?: object }) {
    createFetcher(options);
  }

  return {
    data: computed({
      get: () => fetcher.value.data,
      set: (val) => { fetcher.value.data = val; },
    }),
    error: computed(() => fetcher.value.error),
    pending: computed(() => initializingFetcher.value || fetcher.value.pending),
    hasError: computed(() => fetcher.value.hasError),
    hasNextPage: computed(() => fetcher.value.hasNextPage),
    hasBaseURL: computed(() => fetcher.value.hasBaseURL),
    currentQuery,
    search,
    applyFilters,
    fetchNextPage,
    fetchNextPageDebounced: fetchNextPageDebounced as () => void,
    reset,
  };
}

export function usePageNumberPaginationFetcher<T>(options: { baseURL: string|null, query?: object }) {
  const loading = ref(false);
  const meta = ref<PaginationMetaInfo>({
    count: 0,
    per_page: 100,
    page: 1,
    num_pages: 1,
  });
  const data = ref<T[]>([]);
  const error = ref<any|null>(null);
  const hasError = computed(() => error.value !== null);
  const search = ref<string>('');
  const query = ref<Record<string, string | string[]>>();

  async function resetToStart() {
    meta.value = {
      count: 0,
      per_page: meta.value.per_page,
      page: 1,
      num_pages: 1,
    }
    return await fetchPage();
  }
  const resetToStartDebounced = debounce(resetToStart, 750);

  watch(search, () => resetToStartDebounced());
  watch(query, () => resetToStartDebounced());

  async function fetchPage(opts?: { page?: number }) {
    // False means this call did not produce a page (in flight, missing URL, or the request failed).
    if (loading.value || !options.baseURL) {
      return false;
    }

    try {
      loading.value = true;

      const res = await $fetch<PaginatedResponse<T>>(options.baseURL!, { 
        method: 'GET', 
        params: { 
          ...(options.query || {}), 
          page: opts?.page ?? 1,
          search: search.value,
          ...query.value,
        },
      });
      error.value = null;
      meta.value = res.meta;
      data.value = toRef(res.results).value;
      return true;
    } catch (err: any) {
      error.value = err?.data || 'Could not load data';
      requestErrorToast({ error: err });
      return false;
    } finally {
      loading.value = false;
    }
  }

  return {
    loading,
    meta,
    data,
    error,
    hasError,
    search,
    query: query,
    fetchPage,
  };
}

export function useAccountFilterHash() {
  const route = useRoute();
  const filterQuery = computed(() => filterQueryFromHash(route.hash));
  const filterHash = computed(() => encodeFilterHash(filterQuery.value));
  return { filterQuery, filterHash };
}

export function withFilterQuery(url: string, filters: string) {
  if (!filters) return url;
  return `${url}${url.includes('?') ? '&' : '?'}${filters}`;
}

export async function downloadFilteredCsv(url: string, filters: string) {
  const response = await $fetch.raw<Blob>(withFilterQuery(url, filters), {
    method: 'GET',
    responseType: 'blob',
  });
  const blob = response._data;
  if (!blob) {
    throw new Error('Empty CSV');
  }
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  const disposition = response.headers.get('content-disposition') || '';
  const match = disposition.match(/filename="([^"]+)"/);
  link.download = match?.[1] || 'accounts.csv';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}
